import { useMemo } from 'react';
import { HandPhases } from '@/pages/table/components/BetSlider/BetSlider';
import { useSelector } from 'react-redux';
import { ReducerState } from '../../../services/common/lib/reducerState';

interface ButtonConfiguration {
  label: string;
  bet: number;
}

interface UseRaiseButtonsReturn {
  buttonsToShow: ButtonConfiguration[];
}

interface UseRaiseButtonsProps {
  max: number;
  currentBet: number;
  pot: number;
  bigBlindValue: number;
  handPhase: HandPhases;
  showAllIn: boolean;
}

const formatBet = (bet: number): number => {
  return bet % 1 === 0 ? bet : parseFloat(bet.toFixed(2));
};

const useRaiseButtons = ({
  max,
  currentBet,
  pot,
  bigBlindValue,
  handPhase,
  showAllIn,
}: UseRaiseButtonsProps): UseRaiseButtonsReturn => {
  const userButtonConfigurations = useSelector((state: ReducerState) => state.auth.settings?.buttonConfigurations);
  const onBetButtons = userButtonConfigurations?.onBetButtons || [];
  const onPreFlopButtons = userButtonConfigurations?.onPreFlopButtons || [];
  const postFlopButtons = userButtonConfigurations?.postFlopButtons || [];

  const onBetUserButtons: ButtonConfiguration[] = onBetButtons.map((e) => {
    return {
      label: `x${e.value}`,
      bet: formatBet(e.value * currentBet),
    };
  });

  const onPreFlopUserButtons: ButtonConfiguration[] = onPreFlopButtons.map((e) => {
    return {
      label: `${e.value} BB`,
      bet: formatBet(e.value * bigBlindValue),
    };
  });

  const postFlopUserButtons: ButtonConfiguration[] = postFlopButtons.map((e) => {
    return {
      label: `${e.value * 100}%`,
      bet: formatBet(e.value * pot),
    };
  });

  const buttonConfigurations = useMemo(() => {
    const defaultBetButtons = [
      { label: 'Pot', bet: formatBet(Math.min(pot, max)) },
      { label: 'All In', bet: formatBet(max) },
    ];

    return {
      onBetButtons: [...onBetUserButtons, ...defaultBetButtons],
      onPreFlopButtons: [...onPreFlopUserButtons, ...defaultBetButtons],
      postFlopButtons: [...postFlopUserButtons, ...defaultBetButtons],
    };
  }, [max, onBetUserButtons, onPreFlopUserButtons, postFlopUserButtons, pot, bigBlindValue]);

  const buttonsToShow = useMemo(() => {
    let buttons: { label: string; bet: number }[];
    switch (handPhase) {
      case HandPhases.ON_BET:
        buttons = buttonConfigurations.onBetButtons;
        break;
      case HandPhases.POST_FLOP:
        buttons = buttonConfigurations.postFlopButtons;
        break;
      case HandPhases.PRE_FLOP:
        buttons = buttonConfigurations.onPreFlopButtons;
        break;
      default:
        buttons = [];
    }

    // TODO check filter
    //buttons = buttons.filter((button) => button.bet <= max);

    return showAllIn ? buttons : buttons.filter((button) => button.label !== 'All In');
  }, [handPhase, showAllIn, buttonConfigurations, max]);

  return { buttonsToShow };
};

export default useRaiseButtons;
